/**
 * Dev-only fixture switcher — one click to any layer (localhost only).
 *
 * The maintained save fixtures in `tools/qa/fixtures` are the fastest way to
 * stand in any part of the game without grinding a save, and they were already
 * reachable from the console. This puts them on a strip of buttons, because
 * "which fixture reaches the Operations planner again?" is a question you
 * should not have to answer from memory every time you want to look at it.
 *
 * ONE LIST DRIVES EVERYTHING. `FIXTURES` below is the source for the buttons,
 * the console hint and the not-found error. The previous version of this had
 * the names written out in a comment, in an error string and in a console.info
 * line, and the comment was already the only one listing all five.
 *
 * The real save is stashed under `qaBackupSave` on first use and put back by
 * `restoreSave()` — the fixtures overwrite `idleExtractionSave` in place, so
 * that stash is the only copy of the player's actual game.
 *
 * Fixtures are served from `/fixtures` (see package.json `staticFiles`), which
 * copies them AT SERVER START — regenerate them and the running server keeps
 * handing out the old snapshot until it is restarted.
 */

import { devBar, devButton, isLocal } from './devBar.js';

const SAVE_KEY = 'idleExtractionSave';
const BACKUP_KEY = 'qaBackupSave';
// Which fixture is on screen. Kept in storage rather than sniffed from the
// character name because the point is to survive the reload that loading one
// causes, and a name can be edited in-game.
const ACTIVE_KEY = 'qaFixtureActive';

/**
 * Ordered by how far through the game they sit, so the strip reads as a ladder
 * — that ordering is what makes it answer "take me to layer N" at a glance.
 */
export const FIXTURES = [
    { name: 'fresh', label: 'fresh', layer: 'new account', desc: 'Wipe the save and run the new-account flow' },
    { name: 'mid', label: 'mid', layer: 'Field', desc: 'Pre-Command, level 10 — crosses the Command reveal threshold on boot' },
    { name: 'late', label: 'late', layer: 'Command', desc: 'Post-Command veteran: marks, Legends, upgrades, artifacts' },
    { name: 'pre-founding', label: 'pre-found', layer: 'Syndicate founding', desc: '15 Legends, ceremony still ahead — one-way, so reload to repeat' },
    { name: 'syndicate', label: 'syndicate', layer: 'Syndicate', desc: 'Founded once: Leader, charters, rebuilding era — the L3 starting point' },
    { name: 'grand-tour', label: 'tour', layer: 'Operations', desc: '6 era-Legends + rank-3 charter: one click from the longest operation' },
];

const NAMES = FIXTURES.map(f => f.name);

/**
 * Swap the live save for a fixture and reload.
 *
 * Exported and hung on `window` by main.js — the qa-player skill drives this
 * from the console, and the buttons are just a second caller.
 */
export async function loadFixture(name = 'late') {
    const existing = localStorage.getItem(SAVE_KEY);
    if (existing && !localStorage.getItem(BACKUP_KEY)) {
        localStorage.setItem(BACKUP_KEY, existing); // stash the real save once
    }
    localStorage.setItem(ACTIVE_KEY, name);
    if (name === 'fresh') {
        localStorage.removeItem(SAVE_KEY);
        location.reload();
        return;
    }
    const res = await fetch(`/fixtures/${name}-game.json`, { cache: 'no-store' });
    if (!res.ok) {
        console.error(`Fixture "${name}" not found (${res.status}). Use one of: ${NAMES.join(', ')}.`);
        localStorage.removeItem(ACTIVE_KEY);
        return;
    }
    localStorage.setItem(SAVE_KEY, JSON.stringify(await res.json()));
    location.reload();
}

/**
 * Put back the save that was live before the first loadFixture, and reload.
 *
 * WITH NO BACKUP THIS DOES NOTHING. The previous version fell through to
 * `removeItem(SAVE_KEY)` — so calling restore twice, or calling it before ever
 * loading a fixture, silently WIPED the live save. That was survivable as a
 * console-only function nobody typed by accident; it is not survivable as a
 * button sitting two pixels from six other buttons. Refusing is always right:
 * with nothing stashed there is nothing this function knows how to restore, and
 * deleting the save is not a fallback for that, it is the worst possible one.
 */
export function restoreSave() {
    const backup = localStorage.getItem(BACKUP_KEY);
    if (!backup) {
        console.warn('restoreSave(): nothing stashed — no fixture has been loaded, so the live save is left alone.');
        return false;
    }
    localStorage.setItem(SAVE_KEY, backup);
    localStorage.removeItem(BACKUP_KEY);
    localStorage.removeItem(ACTIVE_KEY);
    location.reload();
    return true;
}

export function initDevFixtures() {
    const bar = devBar();
    if (!bar) return;

    const active = localStorage.getItem(ACTIVE_KEY);
    const hasBackup = !!localStorage.getItem(BACKUP_KEY);

    for (const f of FIXTURES) {
        const btn = devButton({
            label: f.label,
            title: `Dev only — load the "${f.name}" fixture → ${f.layer}.\n${f.desc}\n\nOverwrites the live save; your real one is kept until you press ↩.`,
            onClick: () => loadFixture(f.name),
        });
        // The loaded fixture is marked, because the strip is otherwise the one
        // place in the UI that cannot tell you which save you are looking at —
        // and a fixture you did not mean to be in explains a lot of confusing
        // test results.
        if (f.name === active) {
            btn.style.color = '#fbbf24';
            btn.style.borderColor = '#92400e';
        }
        bar.appendChild(btn);
    }

    // Restore is last and visually apart: it is the way OUT of fixture-land,
    // and pressing it by mistake while reaching for a fixture costs a reload.
    const restore = devButton({
        label: '↩',
        title: hasBackup
            ? 'Dev only — restore the save that was live before the first fixture load'
            : 'Dev only — no stashed save (no fixture has been loaded yet)',
        onClick: () => { if (hasBackup) restoreSave(); },
    });
    restore.style.marginLeft = '4px';
    if (hasBackup) {
        restore.style.color = '#4ade80';
        restore.style.borderColor = '#166534';
    } else {
        restore.style.opacity = '0.4';
        restore.style.cursor = 'default';
    }
    bar.appendChild(restore);
}

/** Console hint, from the same list the buttons come from. */
export function logDevFixtures() {
    if (!isLocal()) return;
    console.info(
        `%cDev fixtures:%c loadFixture('${NAMES.join("'|'")}') · restoreSave() — or use the buttons top-left`,
        'font-weight:bold', ''
    );
}
